import type { GameState } from '@/types';

// ============================================================================
// GAME UTILITY FUNCTIONS
// ============================================================================

// ============================================================================
// FORMATTING UTILITIES
// ============================================================================

/**
 * Format a number with appropriate suffix (K, M, B)
 */
export function formatNumber(num: number): string {
  // Handle NaN and invalid numbers
  if (isNaN(num) || !isFinite(num)) {
    return '0';
  }

  if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';

  // Round to nearest integer for values >= 1, otherwise show up to 3 decimal places
  if (num >= 1) return Math.round(num).toString();

  // For small values, show up to 3 decimal places
  return num.toFixed(3);
}

/**
 * Format duration in milliseconds to human readable string
 */
export function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

/**
 * Format currency with proper handling of negative values and NaN
 */
export function formatCurrency(amount: number): string {
  // Handle NaN and invalid numbers
  if (isNaN(amount) || !isFinite(amount)) {
    return '$0';
  }
  
  const sign = amount < 0 ? '-' : '';
  const absAmount = Math.abs(amount);
  
  if (absAmount >= 1e9) return `${sign}$${(absAmount / 1e9).toFixed(1)}B`;
  if (absAmount >= 1e6) return `${sign}$${(absAmount / 1e6).toFixed(1)}M`;
  if (absAmount >= 1e3) return `${sign}$${(absAmount / 1e3).toFixed(1)}K`;
  if (absAmount >= 1) return `${sign}$${Math.floor(absAmount)}`;
  if (absAmount >= 0.01) return `${sign}$${absAmount.toFixed(2)}`;
  if (absAmount >= 0.001) return `${sign}$${absAmount.toFixed(3)}`;
  return `${sign}$${absAmount.toFixed(4)}`;
}

// ============================================================================
// STORAGE UTILITIES
// ============================================================================

const GAME_STATE_KEY = 'dataEngineeringEmpire_save';

/**
 * Save game state to localStorage
 */
export function saveGameState(state: GameState): void {
  try {
    const saveData = {
      ...state,
      lastSave: Date.now()
    };
    localStorage.setItem(GAME_STATE_KEY, JSON.stringify(saveData));
  } catch (error) {
    console.error('Failed to save game state:', error);
  }
}

/**
 * Load game state from localStorage
 */
export function loadGameState(): GameState | null {
  try {
    const saved = localStorage.getItem(GAME_STATE_KEY);
    if (!saved) return null;

    let state = JSON.parse(saved);

    // Migration: Remove insights, add research progress
    if (state.resources && state.resources.insight !== undefined) {
      delete state.resources.insight;
      if (!state.resources.researchProgress) {
        state.resources.researchProgress = 0;
      }
    }

    if (state.storageCapacity && state.storageCapacity.insight !== undefined) {
      delete state.storageCapacity.insight;
      if (!state.storageCapacity.researchProgress) {
        state.storageCapacity.researchProgress = 100;
      }
    }

    if (state.productionRates && state.productionRates.insight !== undefined) {
      delete state.productionRates.insight;
      if (!state.productionRates.researchProgress) {
        state.productionRates.researchProgress = 0;
      }
    }

    // Migration: Remove scientists, add AGI safety researchers
    if (state.personnel) {
      if (state.personnel.scientist !== undefined) {
        const scientistCount = state.personnel.scientist || 0;
        // Give player some research progress for their scientists (50 per scientist)
        state.resources.researchProgress = (state.resources.researchProgress || 0) + (scientistCount * 50);
        delete state.personnel.scientist;
      }

      if (!state.personnel.agiSafetyResearcher) {
        state.personnel.agiSafetyResearcher = 0;
      }
    }

    // Migration: Add lifetime stats if missing
    if (!state.lifetimeStats) {
      state.lifetimeStats = {
        maxMoney: state.resources.money || 0,
        totalRawDataCollected: state.resources.rawData || 0,
        totalCleanDataProduced: state.resources.cleanData || 0,
        totalFeaturesEngineered: state.resources.feature || 0,
        totalModelsBuilt: state.resources.model || 0,
        totalReasoningEnginesBuilt: state.resources.reasoningEngine || 0,
        totalInfrastructureBuilt: infrastructureCount(state),
        totalPersonnelHired: Object.values(state.personnel).reduce((sum: number, count) => sum + (typeof count === 'number' ? count : 0), 0)
      };
    }

    return validateGameState(state) ? state : null;
  } catch (error) {
    console.error('Failed to load game state:', error);
    return null;
  }
}

/**
 * Clear saved game state
 */
export function clearGameState(): void {
  try {
    localStorage.removeItem(GAME_STATE_KEY);
  } catch (error) {
    console.error('Failed to clear game state:', error);
  }
}

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================


/**
 * Generate random integer between min and max (inclusive)
 */
export function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Check if a value is a valid number
 */
export function isValidNumber(value: any): boolean {
  return typeof value === 'number' && !isNaN(value) && isFinite(value);
}

function validateGameState(state: any): boolean {
  // Add real validation logic as needed
  return typeof state === 'object' && state !== null;
}

// ============================================================================
// SHARING UTILITIES
// ============================================================================

/**
 * Get game stats for sharing - Twitter/X optimized (short)
 */
export function getTwitterShareText(state: GameState): string {
  const playTime = state.gameEndTime
    ? formatDuration(state.gameEndTime - state.gameStartTime)
    : formatDuration(Date.now() - state.gameStartTime);

  const useLifetimeStats = state.gameStatus === 'won' || state.gameStatus === 'lost';

  if (useLifetimeStats && state.gameStatus === 'won') {
    return `🎉 I just achieved AGI in Data Engineering Empire!

🏆 ${state.achievements.length} Achievements
💰 Peak: ${formatCurrency(state.lifetimeStats.maxMoney)}
⏱️ ${playTime}

Can you beat my score? 🚀
https://game.luminousmen.com/

#DataEngineering #AI #Gaming`;
  } else if (useLifetimeStats) {
    return `📊 My Data Engineering Empire Stats:

💰 Peak Money: ${formatCurrency(state.lifetimeStats.maxMoney)}
🤖 Models Built: ${formatNumber(state.lifetimeStats.totalModelsBuilt)}
🏆 ${state.achievements.length} Achievements
⏱️ ${playTime}

Think you can do better? 💪
https://game.luminousmen.com/

#DataEngineering #Gaming`;
  }

  return `🎮 Building my Data Engineering Empire!

💰 ${formatCurrency(state.resources.money || 0)}
🤖 ${Math.floor(state.agiProgress)}% to AGI
🏆 ${state.achievements.length} Achievements

Join me! https://game.luminousmen.com/

#DataEngineering #AI`;
}

/**
 * Get game stats for sharing - LinkedIn optimized (professional)
 */
export function getLinkedInShareText(state: GameState): string {
  const playTime = state.gameEndTime
    ? formatDuration(state.gameEndTime - state.gameStartTime)
    : formatDuration(Date.now() - state.gameStartTime);

  const useLifetimeStats = state.gameStatus === 'won' || state.gameStatus === 'lost';

  if (useLifetimeStats && state.gameStatus === 'won') {
    return `🎯 Just achieved AGI in Data Engineering Empire!

This game brilliantly simulates the challenges of building a modern data infrastructure from scratch. Here's what I accomplished:

📈 Key Metrics:
• Peak Revenue: ${formatCurrency(state.lifetimeStats.maxMoney)}
• Data Pipelines Built: ${state.lifetimeStats.totalInfrastructureBuilt}
• Models Deployed: ${formatNumber(state.lifetimeStats.totalModelsBuilt)}
• Team Members Hired: ${state.lifetimeStats.totalPersonnelHired}
• Time Investment: ${playTime}

Great way to understand data engineering concepts while having fun!

Try it yourself: https://game.luminousmen.com/

#DataEngineering #MachineLearning #AI #DataScience #TechGaming`;
  }

  return `🎮 Exploring Data Engineering Empire

An engaging simulation that teaches real data engineering concepts:
• Building ETL pipelines
• Managing data infrastructure
• Training ML models
• Scaling data operations

Current Progress:
💰 ${formatCurrency(state.resources.money || 0)} revenue
🤖 ${Math.floor(state.agiProgress)}% toward AGI
🏆 ${state.achievements.length} achievements unlocked

Check it out: https://game.luminousmen.com/

#DataEngineering #MachineLearning #DataScience`;
}

/**
 * Get game stats for sharing - Facebook optimized (casual, engaging)
 */
export function getFacebookShareText(state: GameState): string {
  const playTime = state.gameEndTime
    ? formatDuration(state.gameEndTime - state.gameStartTime)
    : formatDuration(Date.now() - state.gameStartTime);

  const useLifetimeStats = state.gameStatus === 'won' || state.gameStatus === 'lost';

  if (useLifetimeStats && state.gameStatus === 'won') {
    return `🎉 VICTORY! I just achieved AGI in Data Engineering Empire! 🤖

This game is AMAZING! You build a data empire from scratch, managing everything from data collection to training AI models.

My Final Stats:
💰 Peak Money: ${formatCurrency(state.lifetimeStats.maxMoney)}
📊 Data Processed: ${formatNumber(state.lifetimeStats.totalCleanDataProduced)} points
🤖 Models Built: ${formatNumber(state.lifetimeStats.totalModelsBuilt)}
🏗️ Infrastructure: ${state.lifetimeStats.totalInfrastructureBuilt} systems
👥 Team Size: ${state.lifetimeStats.totalPersonnelHired} experts
⏱️ Play Time: ${playTime}
🏆 Achievements: ${state.achievements.length}

Think you can beat my score? Challenge accepted? 🚀

Play now: https://game.luminousmen.com/`;
  }

  return `🎮 I'm building my Data Engineering Empire!

Super fun game where you manage data pipelines, build ML models, and work toward creating AGI! 🤖

My Progress:
💰 Money: ${formatCurrency(state.resources.money || 0)}
🤖 AGI Progress: ${Math.floor(state.agiProgress)}%
🏆 Achievements: ${state.achievements.length}
⏱️ Time Played: ${playTime}

Try it out: https://game.luminousmen.com/`;
}

/**
 * Get game stats for sharing - Instagram caption (visual, emoji-heavy)
 */
export function getInstagramShareText(state: GameState): string {
  const playTime = state.gameEndTime
    ? formatDuration(state.gameEndTime - state.gameStartTime)
    : formatDuration(Date.now() - state.gameStartTime);

  const useLifetimeStats = state.gameStatus === 'won' || state.gameStatus === 'lost';

  if (useLifetimeStats && state.gameStatus === 'won') {
    return `✨ ACHIEVEMENT UNLOCKED ✨

🤖 AGI Achieved in Data Engineering Empire! 🎯

My Journey:
💎 Peak Money: ${formatCurrency(state.lifetimeStats.maxMoney)}
📊 Data Collected: ${formatNumber(state.lifetimeStats.totalRawDataCollected)}
🧹 Data Cleaned: ${formatNumber(state.lifetimeStats.totalCleanDataProduced)}
🔧 Features: ${formatNumber(state.lifetimeStats.totalFeaturesEngineered)}
🤖 Models: ${formatNumber(state.lifetimeStats.totalModelsBuilt)}
🏗️ Infrastructure: ${state.lifetimeStats.totalInfrastructureBuilt}
👥 Team: ${state.lifetimeStats.totalPersonnelHired}
⏱️ Time: ${playTime}
🏆 Achievements: ${state.achievements.length}

Think you can beat this? 💪
Link in bio 🔗

#DataEngineering #AI #Gaming #MachineLearning #TechGaming #DataScience #AGI #Achievement #GameDev #IndieGame #WebGame`;
  }

  return `🎮 Building my Data Empire 🚀

💰 ${formatCurrency(state.resources.money || 0)}
🤖 ${Math.floor(state.agiProgress)}% to AGI
🏆 ${state.achievements.length} achievements
⏱️ ${playTime}

The grind is real! 💪✨

Link in bio 🔗

#DataEngineering #Gaming #AI #TechLife #DataScience #MachineLearning #Gamer #IndieGame`;
}

/**
 * Legacy function for backward compatibility
 */
export function getGameStatsForSharing(state: GameState): string {
  return getTwitterShareText(state);
}

/**
 * Share to Twitter/X
 */
export function shareToTwitter(text: string): void {
  const encodedText = encodeURIComponent(text);
  const url = `https://twitter.com/intent/tweet?text=${encodedText}`;
  window.open(url, '_blank', 'width=550,height=420');
}

/**
 * Share to LinkedIn
 */
export function shareToLinkedIn(text: string, url: string = 'https://game.luminousmen.com/'): void {
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);
  const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&summary=${encodedText}`;
  window.open(shareUrl, '_blank', 'width=550,height=550');
}

/**
 * Share to Facebook
 */
export function shareToFacebook(url: string = 'https://game.luminousmen.com/', quote: string): void {
  const encodedUrl = encodeURIComponent(url);
  const encodedQuote = encodeURIComponent(quote);
  const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedQuote}`;
  window.open(shareUrl, '_blank', 'width=550,height=550');
}

/**
 * Copy to clipboard (for Instagram and general use)
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Clipboard write failed:', error);
    return false;
  }
}

/**
 * Share game stats - generic function
 */
export async function shareGameStats(stats: string): Promise<boolean> {
  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Data Engineering Empire Stats',
        text: stats,
        url: 'https://game.luminousmen.com/'
      });
      return true;
    } catch (error) {
      console.error('Share failed:', error);
      return false;
    }
  } else {
    // Fallback to clipboard
    return await copyToClipboard(stats);
  }
} 

export function _hasInfrastructure(state: GameState): boolean {
  return Object.values(state.infrastructure).some(count => (count || 0) > 0);
}

export function infrastructureCount(state: GameState): number {
  return Object.values(state.infrastructure).reduce((total, count) => total + (count || 0), 0);
}
